import React from "react";
import styled from "styled-components";
import Layout from "../components/Layout";
import { GlobalStyle } from "../pages/HomePage";
import { graphql, Link } from "gatsby";
import ShareIcons from "../components/ShareIcons";
import CommentsSection from "../components/CommentsSection";
import LikeButton from "../components/LikeButton";
import Adsense from "react-adsense";
import ReactGA from "react-ga";
import ReactPlayer from "react-player";
import AdCardInDetailPage from "../components/AdCardInDetailPage";
import SEO from "../components/SEO";
import { optimizeImageUrl } from "../lib/prismicImage";

ReactGA.initialize("UA-141669883-1");

/* ─── Styles ─── */

const ArticleSection = styled.section`
  background: var(--bg);
`;

const ArticleHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 1rem;
  @media (min-width: 640px) { padding: 2rem 1.5rem 1rem; }
  @media (min-width: 1024px) { padding: 2rem 2rem 1rem; }
`;

const ArticleTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
  letter-spacing: -0.25px;
  margin: 0 0 0.75rem;
  @media (min-width: 768px) { font-size: 2rem; }
  @media (min-width: 1024px) { font-size: 2.25rem; }
`;

const BannerWrapper = styled.div`
  width: 100%;
  background: var(--bg-secondary);
`;

const BannerImageContainer = styled.div`
  position: relative;
  max-width: 860px;
  margin: 0 auto;
  img {
    width: 100%;
    height: auto;
    max-height: 520px;
    object-fit: contain;
    display: block;
  }
`;

const VideoTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  background: rgba(0,0,0,0.6);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.2s;
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  &:hover { background: rgba(0,0,0,0.85); }
`;

const VideoTitle = styled.h3`
  font-size: 0.9rem;
  color: white;
  margin: 0;
  max-width: 20rem;
`;

const ContentSection = styled.section`
  background: var(--bg-secondary);
  padding: 2rem 0;
`;

const ContentInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) {
    padding: 0 2rem;
    grid-template-columns: 2fr 1fr;
  }
`;

const ArticleBody = styled.div`
  overflow-wrap: break-word;
  word-wrap: break-word;
  min-width: 0;
`;

const Paragraph = styled.p`
  font-family: 'Inter', Georgia, serif;
  font-size: 1.0625rem;
  color: var(--text-secondary);
  line-height: 2;
  margin: 0 0 1.25rem;
  letter-spacing: 0.2px;
`;

const ShareRow = styled.div`
  margin-bottom: 1.25rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  min-width: 0;
  overflow: hidden;
  position: sticky;
  top: 1rem;
  align-self: start;
`;

const RelatedSection = styled.section`
  background: var(--bg);
  border-top: 1px solid var(--border);
  padding: 2rem 0;
`;

const RelatedInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const RelatedTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1.5rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
  @media (min-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
`;

const InGridAd = styled.div`
  grid-column: 1 / -1;
  margin: 0.5rem 0;
`;

const RelatedCardImg = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;
  background: var(--bg-tertiary);
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const RelatedCardTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--compact-title);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
  transition: color 0.2s;
`;

const RelatedCardExternal = styled.a`
  display: block;
  text-decoration: none;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.15); transform: translateY(-2px); }
  &:hover img { transform: scale(1.05); }
  &:hover h3 { color: var(--link); }
`;

const RelatedCardInternal = styled(Link)`
  display: block;
  text-decoration: none;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.15); transform: translateY(-2px); }
  &:hover img { transform: scale(1.05); }
  &:hover h3 { color: var(--link); }
`;


const RelatedCardContent = styled.div`
  padding: 0.75rem;
`;

const VideoOverlay = styled.div`
  position: fixed;
  z-index: 999;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  background: rgba(0,0,0,0.88);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 1rem;
`;

const GoBackBtn = styled.button`
  background: rgba(255,255,255,0.15);
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  cursor: pointer;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.35); }
`;

const AdWrap = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

/* ─── Component ─── */

class Article extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            showVideo: false,
        };
    }

    componentDidMount() {
        const ele = document.getElementById("ipl-progress-indicator");
        if (ele) ele.outerHTML = "";
    }

    render() {
        const prismicArticle = this.props.data.prismicArticle;
        const uidPath = prismicArticle.uid;
        ReactGA.pageview(uidPath);
        ReactGA.event({ category: "Article", action: "Page View" });

        const article = prismicArticle.data;
        const hasImg = article.img && article.img.url;
        const hasEmbed = article.embed && article.embed.embed_url !== null;

        const title = article.title && article.title.text;
        const firstPara = article.content && article.content.raw && article.content.raw[0] && article.content.raw[0].text;
        const description = firstPara
            ? firstPara.slice(0, 160).trim()
            : `Read ${title || "the latest news"} on Andhra Headlines.`;
        const datePublished = article.datetime || prismicArticle.first_publication_date;
        const dateModified = prismicArticle.last_publication_date;
        const tags = (prismicArticle.tags || []).filter(t => t && t !== "featured");
        const relatedArticles = (this.props.data.relatedArticles && this.props.data.relatedArticles.edges) || [];

        return (
            <>
                <SEO
                    title={title}
                    description={description}
                    image={hasImg ? article.img.url : undefined}
                    pathname={`/${uidPath}`}
                    article
                    datePublished={datePublished}
                    dateModified={dateModified}
                    keywords={tags}
                />
                <GlobalStyle />
                <Layout>
                    {/* ── Header ── */}
                    <ArticleSection>
                        <ArticleHeader>
                            <AdWrap className="ad-container">
                                <Adsense.Google
                                    client="ca-pub-5630104718741028"
                                    slot="9699731559"
                                    style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                    format="auto"
                                    responsive="true"
                                />
                            </AdWrap>
                            <ArticleTitle>{article.title && article.title.text}</ArticleTitle>
                        </ArticleHeader>

                        {/* Banner Image */}
                        {hasImg && (
                            <BannerWrapper>
                                <BannerImageContainer>
                                    <img src={optimizeImageUrl(article.img.url, 1000)} alt={article.title ? article.title.text : ''} />
                                    {hasEmbed && (
                                        <VideoTrigger onClick={() => this.setState({ showVideo: true })}>
                                            <svg width="40" height="40" viewBox="0 0 24 24" fill="white" opacity="0.9">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                            <VideoTitle>{article.embed && article.embed.title}</VideoTitle>
                                        </VideoTrigger>
                                    )}
                                </BannerImageContainer>
                            </BannerWrapper>
                        )}
                    </ArticleSection>

                    {/* ── Content ── */}
                    <ContentSection>
                        <ContentInner>
                            <ArticleBody>
                                <ShareRow>
                                    <ShareIcons path={uidPath} />
                                </ShareRow>
                                {article.content && article.content.raw && article.content.raw.map((para, i, arr) => (
                                    <React.Fragment key={i}>
                                        <Paragraph>{para.text}</Paragraph>
                                        {/* Mid-article ad after the 3rd paragraph (only if at least 5 paragraphs exist) */}
                                        {i === 2 && arr.length >= 5 && (
                                            <AdWrap className="ad-container" style={{ margin: '1rem 0 1.5rem' }}>
                                                <Adsense.Google
                                                    client="ca-pub-5630104718741028"
                                                    slot="9699731559"
                                                    style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                                    format="auto"
                                                    responsive="true"
                                                />
                                            </AdWrap>
                                        )}
                                    </React.Fragment>
                                ))}
                                <LikeButton articleId={uidPath} />
                                <CommentsSection articleId={uidPath} />
                                <AdWrap className="ad-container" style={{ marginTop: '1.5rem' }}>
                                    <Adsense.Google
                                        client="ca-pub-5630104718741028"
                                        slot="9699731559"
                                        style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                        format="auto"
                                        responsive="true"
                                    />
                                </AdWrap>
                            </ArticleBody>

                            <Sidebar>
                                <AdCardInDetailPage
                                    client="ca-pub-5630104718741028"
                                    slot="9368762673"
                                    style={{ display: 'block', height: 280, maxWidth: 336, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
                                    layout="-6t+ed+2i-1n-4w"
                                    format="fluid"
                                />
                                <div className="ad-container" style={{ width: '100%', maxWidth: 336 }}>
                                    <Adsense.Google
                                        client="ca-pub-5630104718741028"
                                        slot="9699731559"
                                        style={{ display: 'block', width: '100%', minHeight: 250 }}
                                        format="auto"
                                        responsive="true"
                                    />
                                </div>
                            </Sidebar>
                        </ContentInner>
                    </ContentSection>

                    {/* ── Related Articles ── */}
                    {relatedArticles.filter(e => e.node.uid !== uidPath).length > 0 && (
                        <RelatedSection>
                            <RelatedInner>
                                <RelatedTitle>Related News</RelatedTitle>
                                <RelatedGrid>
                                    {relatedArticles
                                        .filter(e => e.node.uid !== uidPath)
                                        .slice(0, 18)
                                        .reduce((acc, e, i, arr) => {
                                            const a = e.node;
                                            const isExternal = !!(a.data && a.data.third_party_url);
                                            const title = a.data && a.data.title && a.data.title.text ? a.data.title.text : '';
                                            const imgUrl = a.data && a.data.img && a.data.img.url ? a.data.img.url : null;
                                            const CardEl = isExternal ? RelatedCardExternal : RelatedCardInternal;
                                            const linkProps = isExternal
                                                ? { href: a.data.third_party_url.startsWith('http') ? a.data.third_party_url : `https://${a.data.third_party_url}`, target: '_blank', rel: 'noopener noreferrer' }
                                                : { to: `/${a.uid}/` };
                                            acc.push(
                                                <CardEl key={`card-${i}`} {...linkProps}>
                                                    <RelatedCardImg>
                                                        {imgUrl && <img src={optimizeImageUrl(imgUrl, 320)} alt={title} />}
                                                    </RelatedCardImg>
                                                    <RelatedCardContent>
                                                        <RelatedCardTitle>{title}</RelatedCardTitle>
                                                    </RelatedCardContent>
                                                </CardEl>
                                            );
                                            // Inject a full-width ad row after the 6th card (only if more cards follow)
                                            if (i === 5 && arr.length > 6) {
                                                acc.push(
                                                    <InGridAd key="ingrid-ad" className="ad-container">
                                                        <Adsense.Google
                                                            client="ca-pub-5630104718741028"
                                                            slot="9699731559"
                                                            style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                                            format="auto"
                                                            responsive="true"
                                                        />
                                                    </InGridAd>
                                                );
                                            }
                                            return acc;
                                        }, [])
                                    }
                                </RelatedGrid>
                            </RelatedInner>
                        </RelatedSection>
                    )}
                    <div style={{ background: 'var(--bg-secondary)', padding: '1.5rem 0' }}>
                        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1rem' }} className="ad-container">
                            <Adsense.Google
                                client="ca-pub-5630104718741028"
                                slot="9699731559"
                                style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                format="auto"
                                responsive="true"
                            />
                        </div>
                    </div>
                </Layout>

                {/* ── Video Modal ── */}
                {this.state.showVideo && (
                    <VideoOverlay>
                        <VideoContainer>
                            <GoBackBtn onClick={() => this.setState({ showVideo: false })}>
                                ← Go Back
                            </GoBackBtn>
                            <ReactPlayer
                                url={article.embed.embed_url}
                                playing
                                controls
                                volume={0.05}
                                height={500}
                                width="100%"
                                light
                                youtubeConfig={{ playerVars: { showinfo: 1, modestBranding: 1 } }}
                            />
                        </VideoContainer>
                    </VideoOverlay>
                )}
            </>
        );
    }
}

export default Article;

export const pageQuery = graphql`
    query ArticleBySlug($uid: String!, $tags: [String]!) {
        prismicArticle(uid: { eq: $uid }) {
            uid
            tags
            first_publication_date
            last_publication_date
            data {
                third_party_url
                title { text }
                rating
                datetime
                img { url }
                embed {
                    embed_url
                    thumbnail_url
                    author_url
                    title
                }
                content {
                    raw { text }
                }
            }
        }
        relatedArticles: allPrismicArticle(
            filter: {
                tags: { in: $tags }
                uid: { ne: $uid }
                data: { active: { eq: "Y" } }
            }
            sort: { fields: [first_publication_date], order: DESC }
            limit: 21
        ) {
            edges {
                node {
                    uid
                    data {
                        third_party_url
                        title { text }
                        img { url }
                        datetime
                    }
                }
            }
        }
    }
`;
